import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import { bindDialog, usePopupState } from 'material-ui-popup-state/hooks';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import useRouteBlocker from '../../hooks/useBlocker';

const ConfirmationDialog: FC<{ isUnblock?: boolean }> = ({ isUnblock }) => {
  const navigate = useNavigate();
  const {
    formState: { isDirty },
  } = useFormContext();
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'ConfirmationDialog',
  });

  const { nextPathname, onBlockRoute, onUnblockRoute, routeBlocker } =
    useRouteBlocker({ callback: popupState.open });

  const handleStayOnPage = useCallback(() => {
    popupState.close();
  }, [popupState]);

  const handleLeavePage = useCallback(() => {
    popupState.close();
    onUnblockRoute();
    if (routeBlocker.state === 'blocked' && routeBlocker.proceed) {
      routeBlocker.proceed();
    } else if (nextPathname && !isUnblock) {
      navigate(nextPathname, { replace: true });
    }
  }, [
    nextPathname,
    routeBlocker,
    navigate,
    onUnblockRoute,
    popupState,
    isUnblock,
  ]);

  useEffect(() => {
    if (isUnblock) {
      if (routeBlocker.state === 'blocked' && routeBlocker.proceed) {
        routeBlocker.proceed();
      }
      return;
    }
    isDirty ? onBlockRoute() : onUnblockRoute(); // Block navigation if form is dirty
  }, [isDirty, isUnblock, routeBlocker, onBlockRoute, onUnblockRoute]);

  return (
    !isUnblock && (
      <Dialog
        fullWidth
        maxWidth="xs"
        {...bindDialog(popupState)}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleStayOnPage();
          }
        }}
      >
        <DialogTitle>Leave editing without saving?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Changes will be lost</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={handleStayOnPage} variant="text" color="primary">
            Continue editing
          </Button>
          <Button onClick={handleLeavePage} variant="contained" color="error">
            Leave without saving
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

export default ConfirmationDialog;
